import React, { useState, useEffect, useCallback } from "react";
import apiService from "../../services/api/api.service";
import { ChevronDown, ChevronRight, RefreshCcw, Loader } from "lucide-react";

const FinancialData = React.memo(({ uniqueTitle }) => {
  const [balanceSheetType, setBalanceSheetType] = useState("consolidated");
  const [showEquity, setShowEquity] = useState(true);
  const [showNCL, setShowNCL] = useState(true);
  const [showCL, setShowCL] = useState(true);
  const [showAssets, setShowAssets] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFinancialData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getProductByTitle(uniqueTitle);
      if (response.data?.financials?.balanceSheet) {
        setCompanyData({ balanceSheet: response.data.financials.balanceSheet });
      } else {
        setCompanyData(null);
      }
      setError(null);
    } catch (err) {
      setError(
        err.message || "An error occurred while fetching financial data"
      );
    } finally {
      setLoading(false);
    }
  }, [uniqueTitle]);

  useEffect(() => {
    if (uniqueTitle) {
      fetchFinancialData();
    }
  }, [uniqueTitle, fetchFinancialData]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 1,
      notation: "compact",
      compactDisplay: "short",
    })
      .format(value)
      .replace("₹", "₹ ");
  };

  const getYearsFromDates = (dates) => {
    return dates
      .filter((date) => date !== null)
      .map((date) => new Date(date).getFullYear())
      .slice(0, 7);
  };

  const ToggleRow = ({ label, show, onClick }) => (
    <td
      className="p-1 xs:p-2 sm:p-3 font-medium flex items-center cursor-pointer border-r border-gray-300 sticky left-0 bg-white"
      onClick={onClick}
    >
      {show ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      <span className="ml-1">{label}</span>
    </td>
  );

  if (loading)
    return (
      <div className="p-4 text-center text-gray-700 text-sm md:text-base flex justify-center items-center h-64">
        <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
      </div>
    );

  if (error)
    return (
      <div className="p-4 text-red-500 text-sm md:text-base text-center">
        {error}
      </div>
    );

  if (!companyData?.balanceSheet?.[balanceSheetType])
    return (
      <div className="p-4 text-yellow-700 text-sm md:text-base text-center">
        No data available
      </div>
    );

  const years = getYearsFromDates(
    companyData.balanceSheet[balanceSheetType].date
  );
  const balanceSheet = companyData.balanceSheet[balanceSheetType];

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto mb-6">
      <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
        <div className="flex space-x-2">
          <button
            onClick={() => setBalanceSheetType("consolidated")}
            className={`px-2 py-1 sm:px-3 sm:py-1 text-xs sm:text-sm rounded ${
              balanceSheetType === "consolidated"
                ? "bg-blue-100 text-blue-700"
                : "hover:bg-gray-100 text-gray-700"
            }`}
          >
            Consolidated
          </button>
          <button
            onClick={() => setBalanceSheetType("standalone")}
            className={`px-2 py-1 sm:px-3 sm:py-1 text-xs sm:text-sm rounded ${
              balanceSheetType === "standalone"
                ? "bg-blue-100 text-blue-700"
                : "hover:bg-gray-100 text-gray-700"
            }`}
          >
            Standalone
          </button>
        </div>
        <button
          onClick={fetchFinancialData}
          className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-blue-700 border rounded hover:border-blue-400"
        >
          <RefreshCcw size={14} /> Refresh
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[10px] xs:text-xs sm:text-sm border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-100 border-b border-gray-300">
              <th className="p-1 xs:p-2 sm:p-3 text-left font-medium border-r border-gray-300 sticky left-0 bg-gray-100 min-w-[120px]">
                Particulars
              </th>
              {years.map((year) => (
                <th
                  key={year}
                  className="p-1 xs:p-2 sm:p-3 text-right font-medium border-r border-gray-300"
                >
                  {year}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Equity Section */}
            <tr className="border-t border-gray-300">
              <ToggleRow
                label="Equity"
                show={showEquity}
                onClick={() => setShowEquity(!showEquity)}
              />
              {years.map((_, idx) => (
                <td
                  key={idx}
                  className="p-1 xs:p-2 sm:p-3 text-right border-r border-gray-300"
                >
                  {formatCurrency(
                    balanceSheet.equityAndLiability.equity.totalEquity[idx]
                  )}
                </td>
              ))}
            </tr>
            {showEquity &&
              ["shareCapital", "reserves", "otherEquity"].map((item) => (
                <tr className="border-t border-gray-300" key={item}>
                  <td className="p-1 xs:p-2 sm:p-3 pl-4 sm:pl-6 border-r border-gray-300 capitalize">
                    {item.replace(/([A-Z])/g, " $1")}
                  </td>
                  {years.map((_, idx) => (
                    <td
                      key={idx}
                      className="p-1 xs:p-2 sm:p-3 text-right border-r border-gray-300"
                    >
                      {formatCurrency(
                        balanceSheet.equityAndLiability.equity[item][idx]
                      )}
                    </td>
                  ))}
                </tr>
              ))}

            {/* Non-Current Liabilities */}
            <tr className="border-t border-gray-300">
              <ToggleRow
                label="Non-Current Liabilities"
                show={showNCL}
                onClick={() => setShowNCL(!showNCL)}
              />
              {years.map((_, idx) => (
                <td
                  key={idx}
                  className="p-1 xs:p-2 sm:p-3 text-right border-r border-gray-300"
                >
                  {formatCurrency(
                    balanceSheet.equityAndLiability.liabilities.ncl.totalNCL[
                      idx
                    ]
                  )}
                </td>
              ))}
            </tr>
            {showNCL &&
              ["ltBorrowings", "oltLiabilities", "ltProvisions"].map((item) => (
                <tr className="border-t border-gray-300" key={item}>
                  <td className="p-1 xs:p-2 sm:p-3 pl-4 sm:pl-6 border-r border-gray-300 capitalize">
                    {item.replace(/([A-Z])/g, " $1")}
                  </td>
                  {years.map((_, idx) => (
                    <td
                      key={idx}
                      className="p-1 xs:p-2 sm:p-3 text-right border-r border-gray-300"
                    >
                      {formatCurrency(
                        balanceSheet.equityAndLiability.liabilities.ncl[item][
                          idx
                        ]
                      )}
                    </td>
                  ))}
                </tr>
              ))}

            {/* Current Liabilities */}
            <tr className="border-t border-gray-300">
              <ToggleRow
                label="Current Liabilities"
                show={showCL}
                onClick={() => setShowCL(!showCL)}
              />
              {years.map((_, idx) => (
                <td
                  key={idx}
                  className="p-1 xs:p-2 sm:p-3 text-right border-r border-gray-300"
                >
                  {formatCurrency(
                    balanceSheet.equityAndLiability.liabilities.cl.totalCL[idx]
                  )}
                </td>
              ))}
            </tr>
            {showCL &&
              ["stBorrowings", "tradePayables", "ocl", "stProvisions"].map(
                (item) => (
                  <tr className="border-t border-gray-300" key={item}>
                    <td className="p-1 xs:p-2 sm:p-3 pl-4 sm:pl-6 border-r border-gray-300 capitalize">
                      {item.replace(/([A-Z])/g, " $1")}
                    </td>
                    {years.map((_, idx) => (
                      <td
                        key={idx}
                        className="p-1 xs:p-2 sm:p-3 text-right border-r border-gray-300"
                      >
                        {formatCurrency(
                          balanceSheet.equityAndLiability.liabilities.cl[item][
                            idx
                          ]
                        )}
                      </td>
                    ))}
                  </tr>
                )
              )}

            {/* Total Liabilities */}
            <tr className="border-t border-gray-300 font-semibold">
              <td className="p-1 xs:p-2 sm:p-3 sticky left-0 bg-white border-r border-gray-300">
                Total Equity and Liabilities
              </td>
              {years.map((_, idx) => (
                <td
                  key={idx}
                  className="p-1 xs:p-2 sm:p-3 text-right border-r border-gray-300"
                >
                  {formatCurrency(
                    balanceSheet.equityAndLiability.totalEquityAndLiability[idx]
                  )}
                </td>
              ))}
            </tr>

            {/* Assets Section */}
            <tr className="border-t border-gray-300">
              <ToggleRow
                label="Assets"
                show={showAssets}
                onClick={() => setShowAssets(!showAssets)}
              />
              {years.map((_, idx) => (
                <td
                  key={idx}
                  className="p-1 xs:p-2 sm:p-3 text-right border-r border-gray-300"
                >
                  {formatCurrency(balanceSheet.assets.totalAssets[idx])}
                </td>
              ))}
            </tr>
            {showAssets && (
              <>
                <tr className="border-t border-gray-300">
                  <td className="p-1 xs:p-2 sm:p-3 pl-4 sm:pl-6 border-r border-gray-300">
                    Net Fixed Assets
                  </td>
                  {years.map((_, idx) => (
                    <td
                      key={idx}
                      className="p-1 xs:p-2 sm:p-3 text-right border-r border-gray-300"
                    >
                      {formatCurrency(
                        balanceSheet.assets.netFixedAssets.totalNetFixedAssets[
                          idx
                        ]
                      )}
                    </td>
                  ))}
                </tr>
                <tr className="border-t border-gray-300">
                  <td className="p-1 xs:p-2 sm:p-3 pl-4 sm:pl-6 border-r border-gray-300">
                    Capital WIP
                  </td>
                  {years.map((_, idx) => (
                    <td
                      key={idx}
                      className="p-1 xs:p-2 sm:p-3 text-right border-r border-gray-300"
                    >
                      {formatCurrency(balanceSheet.assets.capitalWIP[idx])}
                    </td>
                  ))}
                </tr>
                <tr className="border-t border-gray-300">
                  <td className="p-1 xs:p-2 sm:p-3 pl-4 sm:pl-6 border-r border-gray-300">
                    Other Non-Current Assets
                  </td>
                  {years.map((_, idx) => (
                    <td
                      key={idx}
                      className="p-1 xs:p-2 sm:p-3 text-right border-r border-gray-300"
                    >
                      {formatCurrency(
                        balanceSheet.assets.otherNCA.totalONCA[idx]
                      )}
                    </td>
                  ))}
                </tr>
                <tr className="border-t border-gray-300">
                  <td className="p-1 xs:p-2 sm:p-3 pl-4 sm:pl-6 border-r border-gray-300">
                    Current Assets
                  </td>
                  {years.map((_, idx) => (
                    <td
                      key={idx}
                      className="p-1 xs:p-2 sm:p-3 text-right border-r border-gray-300"
                    >
                      {formatCurrency(balanceSheet.assets.ca.totalCA[idx])}
                    </td>
                  ))}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default FinancialData;
